import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, GraduationCap, BookOpen, Globe, Smartphone, Compass, Key, TrendingUp, ShieldCheck, Ban } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, color, subtext }) => (
  <div style={{ background: 'rgba(88,28,135,0.12)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 14, padding: '24px 20px', display: 'flex', alignItems: 'flex-start', gap: 16 }}>
    <div style={{ width: 48, height: 48, borderRadius: 12, background: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={22} color={color} />
    </div>
    <div>
      <p style={{ fontSize: 28, fontWeight: 800, fontFamily: "'Playfair Display', serif", color: '#fff', lineHeight: 1 }}>{value}</p>
      <p style={{ fontSize: 13, color: '#8885a0', marginTop: 4 }}>{label}</p>
      {subtext && <p style={{ fontSize: 11, color: '#6b6880', marginTop: 2 }}>{subtext}</p>}
    </div>
  </div>
);

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/admin/dashboard`, { withCredentials: true });
        setData(res.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) return <div style={{ color: '#8885a0', textAlign: 'center', padding: 60 }}>Loading dashboard...</div>;
  if (!data) return <div style={{ color: '#ef4444', textAlign: 'center', padding: 60 }}>Failed to load dashboard</div>;

  const { overview, recent_users, category_distribution } = data;

  return (
    <div>
      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16, marginBottom: 32 }}>
        <StatCard icon={Users} label="Total Users" value={overview.total_users} color="#00d4ff" />
        <StatCard icon={ShieldCheck} label="Active Users" value={overview.active_users} color="#22c55e" />
        <StatCard icon={Ban} label="Banned Users" value={overview.banned_users} color="#ef4444" />
        <StatCard icon={Key} label="Active Sessions" value={overview.active_sessions} color="#f59e0b" />
        <StatCard icon={GraduationCap} label="Courses" value={overview.total_courses} color="#8b5cf6" />
        <StatCard icon={BookOpen} label="Books" value={overview.total_books} color="#06b6d4" />
        <StatCard icon={Compass} label="Guides" value={overview.total_guides} color="#ffd700" />
        <StatCard icon={Smartphone} label="Apps" value={overview.total_apps} color="#a855f7" />
        <StatCard icon={Globe} label="Websites" value={overview.total_websites} color="#00d4ff" />
        <StatCard icon={TrendingUp} label="Admin Users" value={overview.admin_users} color="#ec4899" />
      </div>

      {/* Bottom Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Recent Users */}
        <div style={{ background: 'rgba(88,28,135,0.12)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 14, padding: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: '#e0dff0' }}>Recent Registrations</h3>
          {recent_users.length === 0 ? (
            <p style={{ color: '#8885a0', fontSize: 13 }}>No recent registrations</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {recent_users.slice(0, 6).map((u, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid rgba(139,92,246,0.08)' }}>
                  {u.picture ? <img src={u.picture} alt="" style={{ width: 32, height: 32, borderRadius: '50%' }} /> : <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(139,92,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Users size={14} color="#8885a0" /></div>}
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#e0dff0' }}>{u.name}</p>
                    <p style={{ fontSize: 11, color: '#8885a0' }}>{u.email}</p>
                  </div>
                  <span style={{ fontSize: 11, color: '#6b6880' }}>{new Date(u.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Category Distribution */}
        <div style={{ background: 'rgba(88,28,135,0.12)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 14, padding: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: '#e0dff0' }}>Course Categories</h3>
          {category_distribution.length === 0 ? (
            <p style={{ color: '#8885a0', fontSize: 13 }}>No courses yet</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {category_distribution.map((cat, i) => {
                const colors = ['#8b5cf6', '#00d4ff', '#ffd700', '#22c55e', '#ef4444'];
                const maxCount = Math.max(...category_distribution.map(c => c.count));
                return (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 13, color: '#b0adc0' }}>{cat.category}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: colors[i % colors.length] }}>{cat.count}</span>
                    </div>
                    <div style={{ width: '100%', height: 6, background: 'rgba(139,92,246,0.1)', borderRadius: 3 }}>
                      <div style={{ width: `${(cat.count / maxCount) * 100}%`, height: '100%', background: colors[i % colors.length], borderRadius: 3, transition: 'width 0.5s ease' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
